import { groupLeadItems, staticColumnsMatch } from '../../../components/Leads/DynamicFormFields/helper.ts';

function normalizeFieldIdentifier(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * System-managed identifiers must never be entered by an agent. The API
 * generates them when a lead is created.
 */
export function isSystemManagedLeadField(field) {
  if (!field || typeof field !== 'object') return false;
  if (field.is_primary === true) return true;

  return [
    field.key_name,
    field.key_type,
    field.label,
    field.alias_name,
  ].some((value) => normalizeFieldIdentifier(value) === 'leadid');
}

export function isLeadIdField(field) {
  if (!field || typeof field !== 'object') return false;

  return [field.key_name, field.key_type, field.label, field.alias_name].some(
    (value) => normalizeFieldIdentifier(value) === 'leadid'
  );
}

export function isVisibleLeadFormField(field) {
  return Boolean(
    field &&
      field.access_level !== 'none' &&
      field.is_static_column !== true &&
      !isSystemManagedLeadField(field)
  );
}

export function isRequiredLeadFormField(field) {
  return field?.isRequired === true || field?.is_required === true;
}

export function getLeadFormTemplateItems(webForm) {
  if (!webForm) return [];

  const rawItems = webForm?.leads_form_items ?? webForm?.data ?? {};
  return staticColumnsMatch(Object.values(rawItems)).filter(isVisibleLeadFormField);
}

export function buildAgentLeadFormData(webForm, leadRecord = null, options = {}) {
  const includeLeadId = options?.includeLeadId === true;
  const rawItems = staticColumnsMatch(
    Object.values(webForm?.leads_form_items ?? webForm?.data ?? {})
  );
  const leadIdField = rawItems.find(isLeadIdField) || {
    key_name: 'lead_id',
    alias_name: 'Lead ID',
    key_type: 'STRING',
    is_primary: true,
    is_required: false,
    order_id: 1,
    item_id: 1,
  };
  const templateFields = rawItems.filter(isVisibleLeadFormField);
  const sourceData = leadRecord?.data ?? [];
  const fieldMap = new Map(sourceData.map((item) => [item.key_name, item]));

  const leadId =
    leadRecord?.lead_id ||
    leadRecord?.id ||
    fieldMap.get('lead_id')?.value ||
    leadIdField.value ||
    '';

  const populated = templateFields.map((field) => {
    const source = fieldMap.get(field?.key_name);
    return {
      ...field,
      value: source?.value ?? field.value ?? '',
      status_config: source?.status_config ?? field.status_config ?? null,
      entity_data: source?.entity_data ?? field.entity_data ?? null,
    };
  });

  const fields = includeLeadId
    ? [{ ...leadIdField, value: leadId }, ...populated]
    : populated;

  return groupLeadItems(fields, webForm?.group_options || []);
}

export function hasRenderableLeadFormFields(formData) {
  return Object.values(formData || {}).some(
    (group) => Array.isArray(group) && group.length > 0
  );
}
